import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MessageCircle, Phone, X, CheckCircle2, ArrowRight } from 'lucide-react';
import { useApp } from '../context';
import { useNavigate } from 'react-router-dom';

export default function SuggestedInteractionModal() {
  const { state, clearPendingInteraction } = useApp();
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkInteraction = () => {
      // If we have a pending interaction AND the page is visible/focused
      if (state.pendingInteraction && document.visibilityState === 'visible') {
        setTimeout(() => setShow(true), 300);
      }
    };

    // Check immediately in case we're already visible when state updates
    checkInteraction();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkInteraction();
      }
    };

    const handleFocus = () => {
      checkInteraction();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [state.pendingInteraction]);

  if (!state.pendingInteraction) return null;

  const contact = state.contacts.find((c) => c.id === state.pendingInteraction?.contactId);
  if (!contact) return null;

  const handleLog = () => {
    const type = state.pendingInteraction?.type || 'text';
    setShow(false);
    // Navigate to log page with pre-filled data
    navigate(`/log`, { state: { contactId: contact.id, type } });
    clearPendingInteraction();
  };

  const handleSkip = () => {
    setShow(false);
    clearPendingInteraction();
  };

  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            className="w-full max-w-md bg-white rounded-[2.5rem] overflow-hidden shadow-2xl"
          >
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <div className="size-14 rounded-2xl bg-[#fa8585]/10 flex items-center justify-center text-[#fa8585]">
                  {state.pendingInteraction.type === 'call' ? (
                    <Phone size={28} />
                  ) : (
                    <MessageCircle size={28} />
                  )}
                </div>
                <button
                  onClick={handleSkip}
                  className="size-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mb-2">Did you connect?</h3>
              <p className="text-slate-500 leading-relaxed mb-8">
                We noticed you reached out to{' '}
                <span className="font-bold text-slate-900">{contact.name}</span>. Would you like to
                log this interaction?
              </p>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={handleSkip}
                  className="py-4 rounded-2xl font-bold text-slate-400 bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  Skip
                </button>
                <button
                  onClick={handleLog}
                  className="py-4 rounded-2xl font-bold text-white bg-[#fa8585] flex items-center justify-center gap-2 shadow-lg shadow-[#fa8585]/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
                >
                  Log it <ArrowRight size={18} />
                </button>
              </div>
            </div>

            <div className="bg-slate-50 p-4 text-center border-t border-slate-100">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-center gap-2">
                <CheckCircle2 size={12} className="text-emerald-500" />
                Helps track your weekly goal
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
